<template>
  <div class="login-page">
    <AppHeader />
    <main class="main-content">
      <div class="content-wrapper">
        <div class="auth-container">
          <h1>{{ isRegistering ? "Create Account" : "Welcome Back" }}</h1>
          <p
            v-if="serverMessage"
            ref="msgEl"
            :class="{ 'error-message': isError }"
          >
            {{ serverMessage }}
          </p>

          <form ref="formEl" @submit.prevent="handleSubmit">
            <div class="input-group" v-if="isRegistering">
              <label for="nickname">Nickname</label>
              <input
                type="text"
                id="nickname"
                v-model="form.nickname"
                required
                minlength="3"
                autocomplete="nickname"
              />
              <p class="helper-text">
                This will be your unique, permanent in-game name (min 3 chars).
                This is how other players will see you.
              </p>
            </div>
            <div class="input-group">
              <label for="email">Email</label>
              <input
                type="email"
                id="email"
                name="username"
                v-model="form.email"
                required
                autocomplete="username"
              />
            </div>
            <div class="input-group">
              <label for="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                v-model="form.password"
                required
                autocomplete="current-password"
              />
            </div>
            <button
              type="submit"
              class="submit-button"
              :disabled="isSubmitting"
              @pointerdown="onAuthPointer"
            >
              {{
                isSubmitting
                  ? isRegistering
                    ? "Creating account..."
                    : "Signing in..."
                  : isRegistering
                    ? "Register"
                    : "Login"
              }}
            </button>
          </form>

          <div class="divider">OR</div>

          <div class="oauth-actions">
            <button
              type="button"
              tabindex="-1"
              class="oauth-button"
              @click.stop.prevent="loginWithGoogle"
            >
              Continue with Google
            </button>
            <button
              type="button"
              tabindex="-1"
              class="oauth-button"
              @click.stop.prevent="loginWithFacebook"
            >
              Continue with Facebook
            </button>
          </div>
          <p v-if="oauthNotice" class="oauth-notice">{{ oauthNotice }}</p>
          <button
            v-if="oauthNotice"
            type="button"
            tabindex="-1"
            class="oauth-button"
            @click.stop.prevent="loginWithGoogleViaLocalhost"
          >
            Continue via localhost (USB)
          </button>

          <p class="toggle-form">
            {{
              isRegistering
                ? "Already have an account?"
                : "Don’t have an account?"
            }}
            <a href="#" @click.prevent="toggleForm">{{
              isRegistering ? "Log In" : "Sign Up"
            }}</a>
          </p>
        </div>
      </div>
    </main>
    <AppFooter />
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "../store/auth";
import { playSound } from "../utils/audio";
import api from "../services/api";
import { getApiBase, isPrivateLanHost, getLoopbackApiBase, getLoopbackFrontendOrigin } from "../utils/apiBase";
import AppHeader from "../components/AppHeader.vue";
import AppFooter from "../components/AppFooter.vue";

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const isRegistering = ref(false);
const isSubmitting = ref(false);
const formEl = ref(null);
const msgEl = ref(null);
const form = ref({
  email: "",
  password: "",
  nickname: "",
});

const serverMessage = ref("");
const isError = ref(false);
const oauthNotice = ref("");
const LAN_OAUTH_NOTICE =
  "Google OAuth requires a localhost or registered domain. Email and password still work here. For Google on a phone, run USB port forwarding (adb reverse tcp:8080 tcp:8080 and adb reverse tcp:3000 tcp:3000) then use Continue via localhost, or an HTTPS tunnel.";

async function showAuthMessage(text, error) {
  serverMessage.value = text;
  isError.value = Boolean(error);
  await nextTick();
  if (msgEl.value && typeof msgEl.value.scrollIntoView === "function") {
    msgEl.value.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }
}

const setFormStateFromQuery = (query) => {
  if (query.action === "register") {
    isRegistering.value = true;
  } else {
    isRegistering.value = false;
  }
};

onMounted(() => {
  setFormStateFromQuery(route.query);
});

watch(
  () => route.query,
  (newQuery) => {
    setFormStateFromQuery(newQuery);
  }
);

const toggleForm = () => {
  playSound("futuristic-click.mp3");
  isRegistering.value = !isRegistering.value;
  serverMessage.value = "";
  isError.value = false;
  oauthNotice.value = "";
  // --- UPDATED: Reset form
  form.value = { email: "", password: "", nickname: "" };
};

const onAuthPointer = () => {
  if (formEl.value && !formEl.value.checkValidity()) {
    const firstInvalid = [...formEl.value.elements].find(
      (el) => el.willValidate && !el.checkValidity()
    );
    const hint =
      (firstInvalid && firstInvalid.validationMessage) ||
      "Please fill in all required fields.";
    showAuthMessage(hint, true);
  }
};

const handleSubmit = async () => {
  playSound("futuristic-click.mp3");
  if (document.activeElement && typeof document.activeElement.blur === "function") {
    document.activeElement.blur();
  }
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  serverMessage.value = "";
  isError.value = false;

  try {
    if (isRegistering.value) {
      await showAuthMessage("Creating account...", false);
      const response = await authStore.register(form.value);
      await showAuthMessage(
        response.data.message || "Account created. You can log in now.",
        false
      );
      setTimeout(() => toggleForm(), 2000);
    } else {
      await showAuthMessage("Signing in...", false);
      await authStore.login({
        email: form.value.email,
        password: form.value.password,
      });
      try {
        await api.get("/api/user");
      } catch {
        await showAuthMessage(
          "Signed in, but this browser blocked the session cookie. On iPhone use the same site you logged into, or disable Prevent Cross-Site Tracking for this site, then try again.",
          true
        );
        return;
      }
      sessionStorage.setItem("newSession", "true");
      await router.push("/game/home");
    }
  } catch (error) {
    const timedOut =
      error.code === "ECONNABORTED" || /timeout/i.test(error.message || "");
    const network = !error.response;
    await showAuthMessage(
      timedOut
        ? "Request timed out. Check your connection and try again."
        : error.response?.data?.message ||
            (network
              ? "Network error. Could not reach the server."
              : "An unknown error occurred."),
      true
    );
  } finally {
    isSubmitting.value = false;
  }
};

const loginWithGoogle = (event) => {
  playSound("futuristic-click.mp3");
  const btn = event && event.currentTarget;
  if (btn && typeof btn.blur === "function") {
    btn.blur();
  }
  if (isPrivateLanHost(window.location.hostname)) {
    oauthNotice.value = LAN_OAUTH_NOTICE;
    return;
  }
  oauthNotice.value = "";
  const frontendOrigin = window.location.origin;
  window.location.href = `${getApiBase()}/auth/google?frontend=${encodeURIComponent(
    frontendOrigin
  )}`;
};

const loginWithGoogleViaLocalhost = (event) => {
  playSound("futuristic-click.mp3");
  const btn = event && event.currentTarget;
  if (btn && typeof btn.blur === "function") {
    btn.blur();
  }
  const frontendOrigin = getLoopbackFrontendOrigin();
  window.location.href = `${getLoopbackApiBase()}/auth/google?frontend=${encodeURIComponent(
    frontendOrigin
  )}`;
};

const loginWithFacebook = (event) => {
  playSound("futuristic-click.mp3");
  const btn = event && event.currentTarget;
  if (btn && typeof btn.blur === "function") {
    btn.blur();
  }
  alert("Facebook login coming soon!");
};
</script>

<style scoped>
.login-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.main-content {
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 70px; /* Space for fixed header */
}

.content-wrapper {
  width: 100%;
  max-width: 500px; /* This will now be respected */
  padding: 2.5rem;
  background-color: rgba(10, 10, 20, 0.75);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 246, 255, 0.5);
  border-radius: 10px;
  box-shadow: 0 0 25px rgba(0, 246, 255, 0.3);
  text-align: center;
  box-sizing: border-box; /* Ensures padding is included in the width */
}

.auth-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.input-group {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.input-group label {
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: #00f6ff;
}

.input-group input {
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid #00f6ff;
  color: white;
  padding: 10px;
  border-radius: 5px;
}

.submit-button {
  width: 100%;
  margin-top: 1rem;
}

.divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 1rem 0;
  color: #00f6ff;
}

.divider::before,
.divider::after {
  content: "";
  flex: 1;
  border-bottom: 1px solid #00f6ff;
}

.divider:not(:empty)::before {
  margin-right: 0.25em;
}

.divider:not(:empty)::after {
  margin-left: 0.25em;
}

.toggle-form {
  margin-top: 1rem;
}

.error-message {
  color: #ff4d4d;
  font-weight: bold;
}

.oauth-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.oauth-button {
  width: 100%;
}

.oauth-notice {
  color: #ffcc66;
  font-weight: bold;
  font-size: 0.9rem;
  line-height: 1.4;
  margin: 0;
}
</style>
